import { STROKE_SIZES } from "../../shared/default-shape-constants"
import { useDefaultColorTheme, useEditor, useValue } from "tldraw"
import { getGeoShapePath } from "./getGeoShapePath"
import { ShapeFill } from "./ShapeFill"
import { LDGeoShape } from "../../../shared/LDGeoShape"
import { useCurrentPage } from "../../hooks/useCurrentPage"
import { Expr } from "../../../shared/Expr"
import { LDPage } from "../../../shared/LDPage"
import { flattenParams } from "../flattenParams"

export function GeoShapeBody({
  shape,
  shouldScale,
  forceSolid,
}: {
  shape: LDGeoShape
  shouldScale: boolean
  forceSolid: boolean
}) {
  const scaleToUse = shouldScale ? shape.props.scale : 1
  const theme = useDefaultColorTheme()
  const { props } = shape
  const { parameterValues, fill, dash } = props
  const currentPage = useCurrentPage()
  const layer = currentPage.meta.layers[shape.props.layer]

  const path = getGeoShapePath(shape)
  const sw = !shape.props.fill
    ? shape.props.parameterValues.strokeWidth
    : Expr.lit(0)
  const r = shape.props.parameterValues.roundness

  const color = shape.props.fill
    ? shape.props.parameterValues.fillColor
    : shape.props.parameterValues.strokeColor

  const editor = useEditor()

  const params = useValue("params", () => flattenParams(shape, editor), [
    editor,
  ])
  const strokeWidthExpression = layer.layerOverrides?.strokeWidth ?? sw

  const strokeWidth = Expr.evaluate(strokeWidthExpression, params)

  const colorExpression = layer.layerOverrides?.strokeColor ?? color

  const col = Expr.evaluate(colorExpression, params)

  const roundnessExpression = layer.layerOverrides?.roundness ?? r

  const roundness = Expr.evaluate(roundnessExpression, params)

  const fillPath =
    dash === "draw" && !forceSolid
      ? path.toDrawD({
          strokeWidth,
          randomSeed: shape.id,
          passes: 1,
          offset: 0,
          onlyFilled: true,
          roundness: roundness,
        })
      : path.toD({ onlyFilled: true })

  const opacity =
    Expr.evaluate(layer.opacity, params) *
    Expr.evaluate(shape.props.parameterValues.opacity, params)

  const pageMeta = currentPage.meta

  const visible =
    Expr.evaluate(layer.visible, params) &&
    Expr.evaluate(shape.props.parameterValues.visible, params)

  const adjustedOpacity = visible
    ? !pageMeta.focusedLayerId
      ? opacity
      : pageMeta.focusedLayerId === shape.props.layer
        ? opacity + (1 - opacity) / 2
        : opacity * 0.3
    : 0

  return (
    <>
      <ShapeFill
        theme={theme}
        d={fillPath}
        color={Expr.evaluate(color, params)}
        fill={fill ? "fill" : "none"}
        scale={scaleToUse}
        opacity={adjustedOpacity}
      />
      {path.toSvg({
        style: dash,
        strokeWidth,
        roundness: roundness,
        forceSolid,
        randomSeed: shape.id,
        props: {
          fill: "none",
          stroke: col,
          opacity: adjustedOpacity,
        },
      })}
    </>
  )
}
